import { useEffect, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Modal } from "@/components/base/Modal";
import { money, variantLabel } from "@/lib/format";
import { validateOrderQuantity, currenciesDiffer, type SupplierOrderRule } from "@/lib/purchasing";
import * as purchasingApi from "@/lib/api/purchasing";

interface EditDraftPo {
  id: string;
  supplier_id: string | null;
  location_id: string | null;
  currency: string;
  expected_date: string | null;
  notes: string | null;
  supplier_reference: string | null;
  shipping_cost: number | null;
  discount: number | null;
  suppliers?: { name: string } | null;
  locations?: { name: string } | null;
}

interface EditDraftItem {
  id: string;
  product_id: string;
  variant_id: string | null;
  qty_ordered: number;
  unit_cost: number;
  supplier_sku: string | null;
}

interface ProductOption { id: string; name: string; sku: string; }
interface VariantOption { id: string; product_id: string; sku: string | null; option_values: Record<string, string> | null; }
interface SupplierProductRow { supplier_id: string; product_id: string; min_order_qty: number | null; order_multiple: number | null; supplier_price: number | null; supplier_sku: string | null; currency: string | null; }

interface EditLine { uid: string; product_id: string; variant_id: string | null; qty: number; unit_cost: string; supplier_sku: string; }

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

interface Props {
  open: boolean;
  po: EditDraftPo;
  items: EditDraftItem[];
  orgId: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function EditDraftModal({ open, po, items, orgId, onClose, onSaved }: Props) {
  const supplierId = po.supplier_id ?? "";

  const [expectedDate, setExpectedDate] = useState("");
  const [currency, setCurrency] = useState("");
  const [notes, setNotes] = useState("");
  const [supplierReference, setSupplierReference] = useState("");
  const [shippingCost, setShippingCost] = useState("");
  const [discount, setDiscount] = useState("");
  const [lines, setLines] = useState<EditLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Seed the form from the current draft each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setExpectedDate(po.expected_date ?? "");
    setCurrency(po.currency ?? "");
    setNotes(po.notes ?? "");
    setSupplierReference(po.supplier_reference ?? "");
    setShippingCost(po.shipping_cost != null ? String(po.shipping_cost) : "");
    setDiscount(po.discount != null ? String(po.discount) : "");
    setLines(
      items.map((it) => ({
        uid: it.id,
        product_id: it.product_id,
        variant_id: it.variant_id,
        qty: it.qty_ordered,
        unit_cost: it.unit_cost != null ? String(it.unit_cost) : "",
        supplier_sku: it.supplier_sku ?? "",
      })),
    );
    setError("");
  }, [open, po, items]);

  const { data } = useQuery({
    queryKey: ["po-edit-ref", orgId, supplierId],
    enabled: open && !!orgId && !!supplierId,
    queryFn: async () => {
      const [products, variants, supplierProducts] = await Promise.all([
        supabase.from("products").select("id, name, sku").eq("org_id", orgId).neq("status", "archived").order("name"),
        supabase.from("variants").select("id, product_id, sku, option_values").eq("org_id", orgId).neq("status", "archived").order("position"),
        supabase.from("supplier_products").select("supplier_id, product_id, min_order_qty, order_multiple, supplier_price, supplier_sku, currency").eq("org_id", orgId).eq("supplier_id", supplierId),
      ]);
      return {
        products: (products.data ?? []) as ProductOption[],
        variants: (variants.data ?? []) as VariantOption[],
        supplierProducts: (supplierProducts.data ?? []) as SupplierProductRow[],
      };
    },
  });

  const supplierLink = (productId: string) =>
    (data?.supplierProducts ?? []).find((sp) => sp.product_id === productId);

  const orderRuleFor = (productId: string): SupplierOrderRule => {
    const sp = supplierLink(productId);
    return { minOrderQty: sp?.min_order_qty ?? null, orderMultiple: sp?.order_multiple ?? null };
  };

  const orderRuleHint = (productId: string): string => {
    const sp = supplierLink(productId);
    const parts: string[] = [];
    if ((sp?.min_order_qty ?? 0) > 1) parts.push(`minimum ${sp!.min_order_qty}`);
    if ((sp?.order_multiple ?? 0) > 1) parts.push(`multiples of ${sp!.order_multiple}`);
    return parts.length ? `Supplier order rule: ${parts.join(", ")}` : "";
  };

  const lineCurrency = (productId: string): string | null => supplierLink(productId)?.currency ?? null;

  const updateLine = (idx: number, patch: Partial<EditLine>) =>
    setLines((l) => l.map((line, i) => (i === idx ? { ...line, ...patch } : line)));

  const removeLine = (idx: number) => setLines((l) => l.filter((_, i) => i !== idx));

  const addLine = () =>
    setLines((l) => [...l, { uid: crypto.randomUUID(), product_id: "", variant_id: null, qty: 1, unit_cost: "", supplier_sku: "" }]);

  const editTotal = lines.reduce((sum, l) => sum + (Number(l.qty) || 0) * (Number(l.unit_cost) || 0), 0);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (lines.length === 0) {
      setError("Add at least one line item.");
      return;
    }
    if (lines.some((l) => !l.product_id || Number(l.qty) <= 0)) {
      setError("Every line needs a product and a positive quantity.");
      return;
    }
    for (const l of lines) {
      const result = validateOrderQuantity(Number(l.qty), orderRuleFor(l.product_id));
      if (!result.valid) {
        const productName = data?.products.find((p) => p.id === l.product_id)?.name ?? "this product";
        setError(`${productName}: ${result.reason}`);
        return;
      }
    }

    setSaving(true);
    try {
      await purchasingApi.updatePurchaseOrder({
        poId: po.id,
        expectedDate: expectedDate || null,
        currency: currency || po.currency,
        notes: notes || null,
        supplierReference: supplierReference || null,
        shippingCost: Number(shippingCost) || 0,
        discount: Number(discount) || 0,
        lines: lines.map((l) => ({
          product_id: l.product_id,
          variant_id: l.variant_id,
          qty: Number(l.qty),
          unit_cost: Number(l.unit_cost) || 0,
          supplier_sku: l.supplier_sku || null,
        })),
      });
    } catch (err) {
      setSaving(false);
      setError((err as Error).message);
      return;
    }
    setSaving(false);
    onSaved();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit Draft — ${po.suppliers?.name ?? "Purchase Order"}`}
      wide
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </>
      }
    >
      <form onSubmit={handleSave} className="space-y-4">
        {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Supplier</label>
            <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 whitespace-nowrap overflow-hidden text-ellipsis">
              {po.suppliers?.name ?? "—"}
            </div>
          </div>
          <div>
            <label className={labelCls}>Currency</label>
            <input type="text" value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className={inputCls} placeholder="AUD" />
          </div>
          <div>
            <label className={labelCls}>Expected date</label>
            <input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} className={inputCls} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Supplier reference</label>
            <input type="text" value={supplierReference} onChange={(e) => setSupplierReference(e.target.value)} className={inputCls} placeholder="Optional" />
          </div>
          <div>
            <label className={labelCls}>Shipping cost</label>
            <input type="number" min="0" step="0.01" value={shippingCost} onChange={(e) => setShippingCost(e.target.value)} className={inputCls} placeholder="0.00" />
          </div>
          <div>
            <label className={labelCls}>Discount</label>
            <input type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} className={inputCls} placeholder="0.00" />
          </div>
        </div>

        <div>
          <label className={labelCls}>Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500} className={inputCls} placeholder="Optional internal note" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-slate-500">Line items</label>
            <button type="button" onClick={addLine} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap">
              <i className="ri-add-line" /> Add line
            </button>
          </div>
          {lines.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">
              No items yet. Click "Add line" to get started.
            </div>
          ) : (
            <div className="space-y-2">
              {lines.map((l, idx) => {
                const lineVariants = (data?.variants ?? []).filter((v) => v.product_id === l.product_id);
                const ruleHint = orderRuleHint(l.product_id);
                const lc = lineCurrency(l.product_id);
                const currencyMismatch = !!currency && !!lc && currenciesDiffer(lc, currency);
                return (
                  <div key={l.uid}>
                    <div className="flex items-center gap-2">
                      <select
                        value={l.product_id}
                        onChange={(e) => updateLine(idx, { product_id: e.target.value, variant_id: null })}
                        className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                      >
                        <option value="">Select product...</option>
                        {data?.products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                      </select>
                      {lineVariants.length === 1 ? (
                        <span className="w-32 px-3 py-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg truncate whitespace-nowrap">
                          {variantLabel(lineVariants[0].option_values) || lineVariants[0].sku || "Default"}
                        </span>
                      ) : lineVariants.length > 1 ? (
                        <select
                          value={l.variant_id ?? ""}
                          onChange={(e) => updateLine(idx, { variant_id: e.target.value || null })}
                          className="w-32 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                        >
                          <option value="">Select variant…</option>
                          {lineVariants.map((v) => <option key={v.id} value={v.id}>{variantLabel(v.option_values) || v.sku}</option>)}
                        </select>
                      ) : null}
                      <input type="number" min="1" value={l.qty} onChange={(e) => updateLine(idx, { qty: Number(e.target.value) })} className="w-20 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" placeholder="Qty" />
                      <input type="number" min="0" step="0.01" value={l.unit_cost} onChange={(e) => updateLine(idx, { unit_cost: e.target.value })} className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" placeholder="Cost" />
                      <input type="text" value={l.supplier_sku} onChange={(e) => updateLine(idx, { supplier_sku: e.target.value })} className="w-28 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" placeholder="Supplier SKU" />
                      <button type="button" onClick={() => removeLine(idx)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                    {ruleHint && <div className="mt-1 text-xs text-amber-600">{ruleHint}</div>}
                    {currencyMismatch && (
                      <div className="mt-1 text-xs text-red-600">Supplier price is in {lc?.toUpperCase()} but this order is in {currency.toUpperCase()}. Resolve the currency mismatch.</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end text-sm gap-4">
          {currency && <span className="text-xs text-slate-400">Currency: {currency.toUpperCase()}</span>}
          <span className="text-slate-500 mr-2">Total:</span>
          <span className="font-semibold text-slate-900">{money(editTotal, currency)}</span>
        </div>
      </form>
    </Modal>
  );
}